import React, { useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import { LoadingOutlined } from '@ant-design/icons'
import { faDice } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Form,
  Button,
  Input,
  Row,
  Col,
  Radio,
  Checkbox,
  Popover,
  Divider,
  Space,
  Spin,
  Alert,
  Tooltip,
} from 'antd'
import PropTypes from 'prop-types'
import QRCode from 'qrcode.react'

import AntModal from 'components/AntModal'

import { useCharLimit } from 'hooks/useCharLimit'

import generateRandomPassword from 'utils/generateRandomPassword'

import AvatarUpload from '../AvatarUpload'
import UnitList from '../UnitList'
import '@ant-design/compatible/assets/index.css'

const UserFormHooks = ({
  toggleModal,
  handleSubmit,
  loading,
  values,
  isProfile,
  usersList,
  visible,
  acl,
  readOnly = false,
  page = false,
}) => {
  const { data: charLimits } = useCharLimit('cacusuario', [
    'usu_login',
    'usu_nome',
    'usu_email',
  ])
  const [form] = Form.useForm()
  const intl = useIntl()
  const userProfile = useSelector(state => state.user.profile)
  const units = useSelector(state => state.units.data)
  const reduxToken = useSelector(state => state.auth.token)

  // console.log('User Hooks')

  useEffect(() => {
    if (values) {
      form.setFieldsValue({
        usu_nome: values.usu_nome,
        usu_login: values.usu_login,
        usu_email: values.usu_email,
        usu_tipo: values.usu_tipo,
        usu_foto: values.usu_foto,
        usu_ativo: values.usu_ativo === 'S',
        usu_senha: '',
      })
    }
  }, [values, form])

  const handleSubmitEvent = formValues => {
    handleSubmit(formValues)
  }

  const validatePassword = (rule, value) => {
    if (value && value.length < 12) {
      return Promise.reject(
        intl.formatMessage({
          id: 'accessControl.users.validatePassword.minimumCaracters',
        })
      )
    }
    if (value && !/[A-Z]/.test(value)) {
      return Promise.reject(
        intl.formatMessage({
          id: 'accessControl.users.validatePassword.upperCase',
        })
      )
    }
    if (value && !/[a-z]/.test(value)) {
      return Promise.reject(
        intl.formatMessage({
          id: 'accessControl.users.validatePassword.lowerCase',
        })
      )
    }
    if (value && !/\d/.test(value)) {
      return Promise.reject(
        intl.formatMessage({
          id: 'accessControl.users.validatePassword.number',
        })
      )
    }
    if (value && !/[^a-zA-Z0-9]/.test(value)) {
      return Promise.reject(
        intl.formatMessage({
          id: 'accessControl.users.validatePassword.specialCaracter',
        })
      )
    }
    return Promise.resolve()
  }

  const unitsToQrCode = useMemo(
    () =>
      units.map(unit => ({
        ele_descri: unit.ele_descri,
        plt_codigo: unit.plt_codigo,
        cli_codigo: unit.cli_codigo,
      })),
    [units]
  )

  const titlePage = values?.id
    ? intl.formatMessage({ id: 'global.button.update' })
    : intl.formatMessage({ id: 'global.button.insert' })

  const isBlocked = values.usu_bloqueado === 1

  return (
    <AntModal
      width={700}
      title={
        page
          ? intl.formatMessage({
              id: 'global.labels.profile',
            })
          : titlePage
      }
      visible={visible}
      onOk={toggleModal}
      onCancel={() => toggleModal(false)}
      footer={null}
    >
      {!isBlocked ? (
        <Form form={form} layout="vertical" onFinish={handleSubmitEvent}>
          {!!readOnly && (
            <Alert
              message={intl.formatMessage({
                id: 'accessControl.users.cannotEditAdmin',
              })}
              type="info"
              showIcon
              style={{ marginBottom: '24px' }}
            />
          )}

          <Spin
            spinning={loading}
            size="middle"
            indicator={<LoadingOutlined style={{ fontSize: 24 }} />}
          >
            <Row gutter={[16, 16]}>
              <Col sm={6} xs={24}>
                <Form.Item name="usu_foto">
                  <AvatarUpload disabled={readOnly} />
                </Form.Item>
              </Col>
              <Col span={18}>
                <Row>
                  <Col span={24}>
                    <Form.Item
                      name="usu_nome"
                      label={intl.formatMessage({
                        id: 'accessControl.users.name',
                      })}
                      rules={[
                        {
                          required: true,
                          message: intl.formatMessage({
                            id: 'global.labels.requiredField',
                          }),
                        },
                        {
                          max: charLimits?.usu_nome || 50,
                          message: intl.formatMessage(
                            { id: 'global.labels.maxLength' },
                            { count: charLimits?.usu_nome || 50 }
                          ),
                        },
                      ]}
                    >
                      <Input
                        type="text"
                        disabled={readOnly}
                        maxLength={charLimits?.usu_nome || 50}
                        showCount
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col span={10}>
                    <Form.Item
                      name="usu_login"
                      style={{ marginRight: '10px' }}
                      label={intl.formatMessage({
                        id: 'accessControl.users.login',
                      })}
                      rules={[
                        {
                          required: true,
                          message: intl.formatMessage({
                            id: 'global.labels.requiredField',
                          }),
                        },
                        {
                          max: charLimits?.usu_login || 50,
                          message: intl.formatMessage(
                            { id: 'global.labels.maxLength' },
                            { count: charLimits?.usu_login || 50 }
                          ),
                        },
                        {
                          pattern: /^\S*$/,
                          message: intl.formatMessage({
                            id: 'accessControl.users.noSpacesAllowed',
                          }),
                        },
                        {
                          validator: async (rule, value) => {
                            const usersFiltered = usersList.filter(
                              t => t.id_usuario !== values?.id_usuario
                            )

                            if (
                              usersFiltered.find(t => t.usu_login === value)
                            ) {
                              throw new Error(
                                intl.formatMessage({
                                  id: 'global.labels.userExists',
                                })
                              )
                            }
                          },
                        },
                      ]}
                    >
                      <Input
                        type="text"
                        disabled={readOnly}
                        data-testid="login-input"
                        maxLength={charLimits?.usu_login || 50}
                        showCount
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="usu_senha"
                      label={intl.formatMessage({
                        id: 'global.labels.password',
                      })}
                      extra={intl.formatMessage({
                        id: 'accessControl.users.passwordExtra',
                      })}
                      rules={[
                        {
                          required: !values.id,
                          message: intl.formatMessage({
                            id: 'global.labels.requiredField',
                          }),
                        },
                        {
                          validator: validatePassword,
                        },
                      ]}
                    >
                      <Input.Password
                        type="text"
                        placeholder="*********"
                        disabled={readOnly}
                        data-testid="password-input"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={2}>
                    <Form.Item>
                      <Button
                        style={{ marginTop: '30px' }}
                        onClick={() =>
                          form.setFieldsValue({
                            usu_senha: generateRandomPassword(),
                          })
                        }
                        disabled={readOnly}
                      >
                        <FontAwesomeIcon icon={faDice} />
                      </Button>
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="usu_email"
                  label={intl.formatMessage({
                    id: 'accessControl.users.email',
                  })}
                  rules={[
                    {
                      type: 'email',
                      message: intl.formatMessage({
                        id: 'accessControl.users.insertValidEmail',
                      }),
                    },
                    {
                      max: charLimits?.usu_email || 400,
                      message: intl.formatMessage(
                        { id: 'global.labels.maxLength' },
                        { count: charLimits?.usu_email || 400 }
                      ),
                    },
                  ]}
                >
                  <Input
                    type="text"
                    disabled={readOnly}
                    maxLength={charLimits?.usu_email || 400}
                    showCount
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="usu_tipo"
                  label={intl.formatMessage({
                    id: 'accessControl.users.accountType',
                  })}
                  initialValue="1"
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: 'global.labels.requiredField',
                      }),
                    },
                  ]}
                >
                  <Radio.Group
                    disabled={isProfile || readOnly}
                    buttonStyle="solid"
                    data-testid="account-type-radio-group"
                  >
                    {acl?.is('admin') ? (
                      <Radio.Button value="1" data-testid="admin-radio-button">
                        {intl.formatMessage({
                          id: 'accessControl.users.adm',
                        })}
                      </Radio.Button>
                    ) : (
                      <>
                        {!readOnly && !isProfile ? (
                          <Tooltip
                            title={intl.formatMessage({
                              id: values?.id
                                ? 'accessControl.users.cannotEditTypeAdminUser'
                                : 'accessControl.users.cannotCreateAdminUser',
                            })}
                          >
                            <Radio.Button
                              disabled
                              value="1"
                              data-testid="admin-radio-button-disabled"
                            >
                              {intl.formatMessage({
                                id: 'accessControl.users.adm',
                              })}
                            </Radio.Button>
                          </Tooltip>
                        ) : (
                          <Radio.Button
                            disabled
                            value="1"
                            data-testid="admin-radio-button-disabled"
                          >
                            {intl.formatMessage({
                              id: 'accessControl.users.adm',
                            })}
                          </Radio.Button>
                        )}
                      </>
                    )}

                    <Radio.Button value="2" data-testid="user-radio-button">
                      {intl.formatMessage({ id: 'global.labels.user' })}
                    </Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>

            <Row>
              <Col span={24}>
                <Form.Item name="usu_ativo" valuePropName="checked">
                  <Checkbox disabled={isProfile || readOnly}>
                    {intl.formatMessage({
                      id: 'accessControl.users.activeUser',
                    })}
                  </Checkbox>
                </Form.Item>
              </Col>
            </Row>

            {values.id && !isProfile && (
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <UnitList
                    units={values.units}
                    user={values}
                    loadingTable={loading}
                    disabled={readOnly}
                  />
                </Col>
              </Row>
            )}
          </Spin>
          <Row gutter={16}>
            <Divider />
            <Col span={12}>
              <div style={{ float: 'left' }}>
                {!readOnly && (
                  <Space>
                    <Popover
                      content={
                        <QRCode
                          size={384}
                          value={JSON.stringify({
                            token: reduxToken,
                            user: {
                              cli_codigo: userProfile.cli_codigo,
                              plt_codigo: userProfile.plt_codigo,
                              id_usuario: userProfile.id_usuario,
                              usu_nome: userProfile.usu_nome,
                              usu_foto: userProfile.usu_foto,
                            },
                            url: window._env_?.ENV_API_URL,
                            units: unitsToQrCode,
                          })}
                        />
                      }
                    >
                      <Button>
                        {intl.formatMessage({ id: 'global.button.qrCode' })}
                      </Button>
                    </Popover>

                    <Popover
                      content={
                        <QRCode
                          size={384}
                          value={JSON.stringify({
                            apiUrl: window._env_?.ENV_API_URL,
                            username: userProfile.usu_login,
                          })}
                        />
                      }
                    >
                      <Button>
                        {intl.formatMessage({ id: 'global.button.qrCode' })} App
                      </Button>
                    </Popover>
                  </Space>
                )}
              </div>
            </Col>

            <Col span={12}>
              <div style={{ float: 'right' }}>
                <Form.Item style={{ margin: '0px' }}>
                  <Space>
                    <Button
                      type="default"
                      htmlType="button"
                      onClick={() => toggleModal()}
                    >
                      {intl.formatMessage({ id: 'global.button.cancel' })}
                    </Button>
                    <Button
                      loading={loading}
                      type="primary"
                      htmlType="submit"
                      disabled={readOnly}
                    >
                      {intl.formatMessage({ id: 'global.button.save' })}
                    </Button>
                  </Space>
                </Form.Item>
              </div>
            </Col>
          </Row>
        </Form>
      ) : (
        <Form form={form} layout="vertical" onFinish={handleSubmitEvent}>
          <Row gutter={[16, 16]}>
            <Col sm={6} xs={24}>
              <Form.Item name="usu_foto">
                <AvatarUpload />
              </Form.Item>
              <Form.Item name="usu_ativo" valuePropName="checked">
                <Checkbox disabled={isProfile || readOnly}>
                  {intl.formatMessage({
                    id: 'accessControl.users.activeUser',
                  })}
                </Checkbox>
              </Form.Item>
            </Col>
            <Col span={18}>
              <Row>
                <Col span={20}>
                  <Form.Item
                    name="usu_nome"
                    label={intl.formatMessage({
                      id: 'accessControl.users.name',
                    })}
                  >
                    <Input disabled type="text" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={20}>
                  <Form.Item
                    name="usu_login"
                    label={intl.formatMessage({
                      id: 'accessControl.users.login',
                    })}
                  >
                    <Input disabled type="text" />
                  </Form.Item>
                </Col>
                <Col span={20}>
                  <Form.Item
                    name="usu_email"
                    label={intl.formatMessage({
                      id: 'accessControl.users.email',
                    })}
                  >
                    <Input disabled type="text" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={20}>
                  <Form.Item
                    name="usu_senha"
                    label={intl.formatMessage({
                      id: 'global.labels.password',
                    })}
                    extra={intl.formatMessage({
                      id: 'accessControl.users.passwordCorrection',
                    })}
                    rules={[
                      {
                        required: true,
                        message: intl.formatMessage({
                          id: 'global.labels.requiredField',
                        }),
                      },
                      {
                        validator: validatePassword,
                      },
                    ]}
                  >
                    <Input.Password
                      type="text"
                      placeholder="*********"
                      disabled={readOnly}
                      data-testid="password-input-blocked"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
          <Divider />
          <Row>
            <Col span={24}>
              <div style={{ float: 'right' }}>
                <Form.Item style={{ margin: '0px' }}>
                  <Space>
                    <Button
                      type="default"
                      htmlType="button"
                      onClick={() => toggleModal()}
                    >
                      {intl.formatMessage({ id: 'global.button.cancel' })}
                    </Button>
                    <Button
                      loading={loading}
                      type="primary"
                      htmlType="submit"
                      disabled={readOnly}
                    >
                      {intl.formatMessage({
                        id: 'accessControl.users.unblockUser',
                      })}
                    </Button>
                  </Space>
                </Form.Item>
              </div>
            </Col>
          </Row>
        </Form>
      )}
    </AntModal>
  )
}

UserFormHooks.defaultProps = {
  loading: false,
  isProfile: false,
  readOnly: false,
  page: false,
  visible: false,
}

UserFormHooks.propTypes = {
  toggleModal: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  acl: PropTypes.shape({
    is: PropTypes.func,
  }).isRequired,
  isProfile: PropTypes.bool,
  loading: PropTypes.bool,
  usersList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  values: PropTypes.shape({
    id: PropTypes.number,
    id_usuario: PropTypes.number,
    units: PropTypes.arrayOf(PropTypes.shape({})),
    usu_bloqueado: PropTypes.number,
    usu_nome: PropTypes.string,
    usu_login: PropTypes.string,
    usu_email: PropTypes.string,
    usu_tipo: PropTypes.string,
    usu_foto: PropTypes.string,
    usu_ativo: PropTypes.string,
  }).isRequired,
  visible: PropTypes.bool,
  page: PropTypes.bool,
}

export default UserFormHooks
